/**
 * The resumed session checked against the server's own sets (T-188).
 *
 * ── WHY THE DEVICE CAN KNOW LESS THAN THE SERVER ───────────────────────────
 * `@capacitor/preferences` on Android writes with `SharedPreferences.apply()`:
 * the promise resolves once the value is in memory, and the disk write
 * follows later — it is forced only when the activity stops. The outbox sends
 * a set as soon as its own write resolves, so the server can store the set
 * before either the session or the queue is on disk. A kill in that window
 * loses both locally, keeps the set on the server, and nothing replays: the
 * workout resumes one set behind, and the person logs the "missing" set again
 * as a duplicate. Measured on the container: a resolved `Preferences.set`
 * force-stopped about 150 ms later was gone after the relaunch in 4 of 6
 * tries, and 2 of 21 kills after "Log set" left the server one set ahead with
 * an empty outbox. Both keys live in the same file, so no ordering of the two
 * writes cures it.
 *
 * ── WHO IS RIGHT ───────────────────────────────────────────────────────────
 * Every row of this session carries an id this device generated (D19), and a
 * session is open on one device at a time, so a row the device does not know
 * was logged here and lost here. It is ADDED; the device keeps the cursor,
 * the scratch numbers and every set of its own, including one the server does
 * not list (`adoptServerSets` says why nothing is ever removed).
 *
 * ── WHAT THIS DOES NOT CURE ────────────────────────────────────────────────
 * A kill after the tap and before BOTH the flush and the POST loses the set
 * on the device and on the server alike, and there is nothing left anywhere
 * to take back. On the container the first flush after "Log set" landed 140
 * to 650 ms after the tap (median about 180), and a kill issued within
 * 600 ms of the tap lost the set outright in 39 of 92 trials. D163's "the
 * active session survives the app being killed" has that hole on Android for
 * as long as the session and the queue are written with `apply()`. Closing it
 * is a storage decision — a write that is on disk when its promise resolves —
 * and not something a reconcile can do.
 *
 * ── WHEN, EXACTLY ──────────────────────────────────────────────────────────
 * Once per session per launch, and only while NOTHING of the session is
 * queued, checked again after the answer: a delete or a correction still
 * waiting would make the server's list disagree with the device on purpose.
 * The start is one of those items, so a session the server has never seen is
 * never asked about.
 */

import type { OutboxState } from "@repo/client/offline";
import type {
  SessionSetsOutput,
  SessionSetView,
} from "@repo/contract/training";
import type { ServerSet, WorkoutSession } from "./session-model";

/** An active session with none of its writes left in the queue. */
export function isSetsReconcileDue(
  session: WorkoutSession | null,
  outbox: OutboxState,
  done: ReadonlySet<string>
): session is WorkoutSession {
  if (
    session === null ||
    session.ended !== null ||
    done.has(session.sessionId)
  ) {
    return false;
  }
  return !outbox.items.some(
    (item) =>
      item.entityId === session.sessionId ||
      item.dependsOn === session.sessionId
  );
}

export function serverSetOf(view: SessionSetView): ServerSet {
  return {
    distanceMetres: view.distanceMetres ?? null,
    durationSeconds: view.durationSeconds ?? null,
    exerciseId: view.exerciseId,
    id: view.setId,
    // `D209`: a warm-up adopted from the server stays one. `null` and absent
    // are both a working set on both sides, stored as absent.
    ...(view.kind ? { kind: view.kind } : {}),
    load: view.load,
    performedAt: Date.parse(view.performedAt),
    reps: view.reps,
    rpe: view.rpe,
    ...(view.side ? { side: view.side } : {}),
  };
}

export interface SetsReconcileDeps {
  /** `fetchSessionSets(api, sessionId)` from `@repo/client/data/workout`. */
  readonly fetchSets: (sessionId: string) => Promise<SessionSetsOutput>;
  /**
   * Still due now that the answer is here, AND no set moved meanwhile: a set
   * logged and sent while the request was out is missing from its answer,
   * and would read as a set the server no longer shows.
   */
  readonly isStillDue: (asked: WorkoutSession) => boolean;
  /** The store's `reconcileSets`. */
  readonly reconcile: (
    sessionId: string,
    sets: readonly ServerSet[]
  ) => Promise<void>;
}

/**
 * One check. `"done"` settles the session for this launch, including a 404 —
 * the server does not hold it, so there is nothing to take in. `"moved"` is a
 * session whose sets or queue changed while the answer was on its way;
 * `"failed"` a request that did not answer. Both are tried again on the next
 * change.
 */
export async function reconcileSessionSets(
  session: WorkoutSession,
  deps: SetsReconcileDeps
): Promise<"done" | "moved" | "failed"> {
  let answer: SessionSetsOutput;
  try {
    answer = await deps.fetchSets(session.sessionId);
  } catch (error) {
    if (error instanceof Object && "status" in error && error.status === 404) {
      return "done";
    }
    return "failed";
  }
  if (!deps.isStillDue(session)) {
    return "moved";
  }
  await deps.reconcile(session.sessionId, answer.sets.map(serverSetOf));
  return "done";
}
